import React, { useState, useRef, useCallback } from 'react';
import s from './s.module.less';
import Avatar from './imgs/avatar.png';
import Spark from './imgs/sparkles.svg';
import classNames from 'classnames';
import { EAIChatPageId, EAiRole, TAiMessage, TAIUploadImage } from 'types/ai';
import MessageItem from '../MessageItem';
import ChatLoading from '../ChatLoading';
import MarkdownRenderer from 'components/MarkdownRenderer';
import TextArea from 'antd/es/input/TextArea';
import { Button, message, Modal, Spin } from 'antd';
import {
    chatWithBot,
    feedbackMessage,
    getHistoryMessage,
    getSuggestedQuestions,
    TChatWithBotParams,
    uploadImage,
} from 'api/aiChat';
import { BOT_ID } from 'constants/common';
import { getUnitId } from 'utils/localstore';
import dayjs from 'dayjs';
import { getAiChatHistoryMessage, setAiChatHistoryMessage } from 'utils/sessionStorage';
import { useNavigate } from 'react-router-dom';
import { PATH } from 'constants/path';

type Props = {
    chatWrapClassName?: string;
    onClose?: () => void;
    pageId?: EAIChatPageId;
    initialMessage?: string;
};

interface TFormatedImage {
    file: File;
    url: string;
    id: string;
}

const historyMessage: TAiMessage[] = [
    {
        role: EAiRole.ASSISTANT,
        content:
            "👋  Hi! I'm Olivia, your onboarding AI Agent.I’ll help you get set up on Klarity Health quickly so you can start seeing patients.How can I help you today?",
        timestamp: dayjs().add(3, 'minutes').format('YYYY-MM-DDTHH:mm:ss.SSS'),
        bot_id: 'provider_onboarding',
    },
    {
        role: EAiRole.ASSISTANT,
        content: 'Here are just few of the many things you can ask me about.',
        timestamp: dayjs().add(4, 'minutes').format('YYYY-MM-DDTHH:mm:ss.SSS'),
        bot_id: 'provider_onboarding',
    },
];

enum EFeedbackResult {
    LIKE = 'like',
    UNLIKE = 'unlike',
}

enum EBadReason {
    ANSWER_INCORRECT = 'Answer was incorrect',
    NOT_DETAILED_ENOUGH = 'Not detailed enough',
    DID_NOT_ADDRESS_MY_QUESTION = "Didn't address my question",
    HARD_TO_UNDERSTAND = 'Hard to understand',
}

export default function AiChatContent(props: Props) {
    const {
        chatWrapClassName,
        onClose,
        pageId = EAIChatPageId.PROVIDER_ONBOARDING_EMAIL_ENTRY,
        initialMessage,
    } = props;
    const navigate = useNavigate();
    const [inputValue, setInputValue] = useState('');

    const historyMessageListRef = useRef<TAiMessage[]>(getAiChatHistoryMessage());
    const [showHistoryMessage, setShowHistoryMessage] = useState(false);

    const messageListRef = useRef<TAiMessage[]>([]);
    const [forceUpdateFlag, forceUpdate] = useState({});
    const [formatedImages, setFormatedImages] = useState<TFormatedImage[]>([]);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const pendingAIReplyMessage = useRef<TAiMessage>();
    const pendingSuggestedMessage = useRef<TAiMessage>();

    const [showFeedback, setShowFeedback] = useState(false);
    const [feedbackResult, setFeedbackResult] = useState<EFeedbackResult>();
    const [feedbackMessageId, setFeedbackMessageId] = useState<number>();
    const [hasFeedbackComment, setHasFeedbackComment] = useState(false);
    const [isFeedbackLoading, setIsFeedbackLoading] = useState(false);

    const [badReason, setBadReason] = useState<EBadReason>();
    const [badReasonComment, setBadReasonComment] = useState<string>();
    const [isTyping, setIsTyping] = useState(false);
    const [isSending, setIsSending] = useState(false);

    const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);

    const currentScrollBottomRef = useRef<number>(0);

    const allFiles = useRef<File[]>([]);

    const messageList = messageListRef.current;
    const historyMessageList = historyMessageListRef.current || [];
    const hasHistoryMessageList = historyMessageList.length > 0;

    const [previewImageUrl, setPreviewImageUrl] = useState<string>();

    const latestUserMessage = React.useRef('');
    const hasSentInitialMessage = React.useRef(false);
    const isInitialMessageReply = React.useRef(false);
    const hasFetchedSuggestedQuestionsAfterInitial = React.useRef(false);

    const revertExtraMsg =
        pendingAIReplyMessage.current &&
        pendingSuggestedMessage.current &&
        pendingSuggestedMessage.current.timestamp &&
        pendingAIReplyMessage.current.timestamp &&
        dayjs(pendingSuggestedMessage.current.timestamp).isBefore(dayjs(pendingAIReplyMessage.current.timestamp));

    const removeSuggestedMessage = () => {
        messageListRef.current = messageListRef.current.filter((item) => {
            return item.role !== EAiRole.QUESTION;
        });
        forceUpdate({});
    };

    const moveSuggestToNewMessage = (data?: string[]) => {
        const questions = data || suggestedQuestions;
        const hasSuggest = questions && questions.length > 0;
        if (hasSuggest) {
            const newMsg: TAiMessage = {
                role: EAiRole.QUESTION,
                content: questions.join('---'),
                timestamp: pendingSuggestedMessage?.current?.timestamp || dayjs().format('YYYY-MM-DDTHH:mm:ss.SSS'),
                bot_id: BOT_ID.PROVIDER_ONBOARDING,
            };
            pendingSuggestedMessage.current = undefined;
            messageListRef.current = messageListRef.current.filter((item) => {
                return item.role !== EAiRole.QUESTION;
            });
            messageListRef.current.push(newMsg);
            setSuggestedQuestions([]);
        }
        messageListRef.current.sort((a, b) => {
            return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
        });
        forceUpdate({});
    };

    const scrollToBottom = () => {
        setTimeout(() => {
            const messageWrap = document.getElementById('ai-message-wrap');
            if (messageWrap) {
                messageWrap.scrollTo({
                    top: messageWrap.scrollHeight,
                    behavior: 'smooth',
                });
            }
        }, 300);
    };

    const initHistoryMessage = async () => {
        const hMessage = getAiChatHistoryMessage();
        if (hMessage.length > 0) {
            historyMessageListRef.current = hMessage;
        } else {
            const res1 = await getHistoryMessage({
                user_id: getUnitId(),
                date_str: dayjs().add(-1, 'day').format('YYYYMMDD'),
            });
            const res = await getHistoryMessage({
                user_id: getUnitId(),
                date_str: dayjs().format('YYYYMMDD'),
            });
            const _data: TAiMessage[] = [];
            if (!res1.error) {
                const yesterdayData = res1.data?.data?.messages;
                if (yesterdayData && yesterdayData.length > 0) {
                    _data.push(...yesterdayData);
                }
            }
            if (!res.error) {
                const todayData = res.data?.data?.messages;
                if (todayData && todayData.length > 0) {
                    _data.push(...todayData);
                }
            }
            historyMessageListRef.current = _data;
            forceUpdate({});
        }
    };

    React.useEffect(() => {
        if (showHistoryMessage && currentScrollBottomRef.current) {
            setTimeout(() => {
                const messageWrap = document.getElementById('ai-message-wrap');
                if (messageWrap) {
                    messageWrap.scrollTo({
                        top: messageWrap.scrollHeight - currentScrollBottomRef.current - 100,
                        behavior: 'auto',
                    });
                }
                currentScrollBottomRef.current = 0;
            }, 10);
        }
    }, [showHistoryMessage]);

    React.useEffect(() => {
        scrollToBottom();
        initHistoryMessage();
    }, []);

    // 自适应高度调整
    const adjustHeight = useCallback(() => {
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.style.height = 'auto';
            const { scrollHeight } = textarea;
            const maxHeight = 120; // 最大高度
            textarea.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
        }
    }, []);

    // 处理文本输入
    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setInputValue(e.target.value);
        adjustHeight();
    };

    // 处理图片粘贴
    const handlePaste = async (e: React.ClipboardEvent) => {
        const items = e.clipboardData?.items;
        if (!items) return;

        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item.type.indexOf('image') !== -1) {
                try {
                    e.preventDefault();
                    const file = item.getAsFile();
                    if (file) {
                        allFiles.current.push(file);
                        const url = URL.createObjectURL(file);
                        const newImage: TFormatedImage = {
                            file,
                            url,
                            id: Date.now().toString() + Math.random(),
                        };
                        setFormatedImages((prev) => [...prev, newImage]);
                    }
                } catch (e: any) {
                    console.error(e);
                }
            }
        }
    };

    // 删除图片
    const removeImage = (id: string) => {
        setFormatedImages((prev) => {
            const imageToRemove = prev.find((img) => img.id === id);
            if (imageToRemove) {
                URL.revokeObjectURL(imageToRemove.url);
            }
            return prev.filter((img) => img.id !== id);
        });
    };

    // 处理文件选择
    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { files } = e.target;
        if (!files) return;

        Array.from(files).forEach((file) => {
            if (file.type.startsWith('image/')) {
                const url = URL.createObjectURL(file);
                const newImage: TFormatedImage = {
                    file,
                    url,
                    id: Date.now().toString() + Math.random(),
                };
                allFiles.current.push(file);
                setFormatedImages((prev) => [...prev, newImage]);
            }
        });

        // 清空input value，允许选择相同文件
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // 触发文件选择
    const handleAddClick = () => {
        fileInputRef.current?.click();
    };

    // 获取建议问题的函数
    const fetchSuggestedQuestions = React.useCallback(async (targetPageId: EAIChatPageId) => {
        pendingSuggestedMessage.current = {
            role: EAiRole.QUESTION,
            content: '',
            timestamp: dayjs().format('YYYY-MM-DDTHH:mm:ss.SSS'),
            bot_id: BOT_ID.PROVIDER_ONBOARDING,
        };
        removeSuggestedMessage();
        forceUpdate({});
        scrollToBottom();
        const res = await getSuggestedQuestions(targetPageId);
        if (!res.error && res.data?.data && res.data?.data.length > 0) {
            const data: string[] = res?.data?.data;
            setSuggestedQuestions(data);
            pendingSuggestedMessage.current.content = data.join('---');
            forceUpdate({});
            //moveSuggestToNewMessage(data);
        } else {
            pendingSuggestedMessage.current = undefined;
            forceUpdate({});
        }
    }, []);

    // 初始消息回复后获取建议问题
    const fetchSuggestedQuestionsAfterReply = React.useCallback(async () => {
        await fetchSuggestedQuestions(EAIChatPageId.PROVIDER_HOME_APPOINTMENT_TRENDS);
        // 重置标记，以便后续消息不会再次触发
        isInitialMessageReply.current = false;
    }, [fetchSuggestedQuestions]);

    const sendMessageToServer = async (content: string, fileIds?: string[]) => {
        if (isSending) {
            return;
        }
        setShowFeedback(false);
        setFeedbackResult(undefined);
        setFeedbackMessageId(undefined);
        setHasFeedbackComment(false);
        setIsSending(true);
        setIsTyping(true);
        try {
            const data: TChatWithBotParams = {
                bot_id: BOT_ID.PROVIDER_ONBOARDING,
                user_input: content,
                user_id: getUnitId(),
            };
            latestUserMessage.current = content;
            if (fileIds && fileIds.length > 0) {
                data.file_ids = fileIds;
            }
            if (pageId) {
                data.page_id = pageId;
            }
            const response: any = await chatWithBot(data);

            if (!response.ok) {
                message.error(`A connection error occurred. Please try again.`);
            }

            console.log('📥 Received SSE response:', response.status);

            // Handle SSE stream
            const reader = response.body?.getReader();
            const decoder = new TextDecoder();
            let streamedContent = '';
            let metadata: any = {};

            if (reader) {
                try {
                    while (true) {
                        // eslint-disable-next-line no-await-in-loop
                        const { done, value } = await reader.read();
                        if (done) break;

                        const chunk = decoder.decode(value, { stream: true });
                        const lines = chunk.split('\n');

                        for (const line of lines) {
                            if (line.startsWith('data: ')) {
                                try {
                                    const eventData = JSON.parse(line.slice(6));

                                    if (eventData.type === 'connected') {
                                        console.log('🔗 Connected to agent:', eventData.agent_id);
                                        setIsSending(false);
                                    } else if (eventData.type === 'content') {
                                        streamedContent += eventData.content;
                                        if (pendingAIReplyMessage.current) {
                                            // Update the streaming message in real-time
                                            pendingAIReplyMessage.current.content = streamedContent;
                                            forceUpdate({});
                                        }
                                    } else if (eventData.type === 'done') {
                                        metadata = eventData.metadata || {};
                                        setShowFeedback(true);
                                        scrollToBottom();
                                        console.log('✅ Stream completed with metadata:', metadata);
                                        // 如果是初始消息的回复完成，调用 suggested-questions 接口
                                        if (
                                            isInitialMessageReply.current &&
                                            !hasFetchedSuggestedQuestionsAfterInitial.current
                                        ) {
                                            hasFetchedSuggestedQuestionsAfterInitial.current = true;
                                            fetchSuggestedQuestionsAfterReply();
                                        }
                                        break;
                                    } else if (eventData.type === 'error') {
                                        throw new Error(eventData.error || 'Unknown error');
                                    }
                                } catch (parseError) {
                                    console.warn('Failed to parse SSE event:', line);
                                }
                            }
                        }
                    }
                } finally {
                    reader.releaseLock();
                    setIsTyping(false);
                    setIsSending(false);
                }
            }
        } catch (e) {
            console.error(e);
            return null;
        } finally {
            setIsTyping(false);
            setIsSending(false);
        }
    };

    const handleClickSendMessage = async (content: string, _images?: File[]) => {
        if (isSending) {
            return;
        }
        const hasImageFile = formatedImages && formatedImages.length > 0;
        moveSuggestToNewMessage();
        // 如果不是初始消息，重置标记
        if (!isInitialMessageReply.current) {
            hasFetchedSuggestedQuestionsAfterInitial.current = false;
        }
        if (pendingAIReplyMessage.current?.content) {
            messageListRef.current.push({
                role: EAiRole.ASSISTANT,
                content: pendingAIReplyMessage.current.content,
                timestamp: pendingAIReplyMessage?.current.timestamp || dayjs().format('YYYY-MM-DDTHH:mm:ss.SSS'),
                bot_id: BOT_ID.PROVIDER_ONBOARDING,
            });

            pendingAIReplyMessage.current = undefined;
        }

        const newAddMessageFromUser: TAiMessage = {
            role: EAiRole.USER,
            content,
            timestamp: dayjs().add(1, 'second').format('YYYY-MM-DDTHH:mm:ss.SSS'),
            bot_id: BOT_ID.PROVIDER_ONBOARDING,
        };

        if (hasImageFile) {
            newAddMessageFromUser.blobs = formatedImages?.map((item, inx) => {
                const mapItem: TAIUploadImage = {
                    id: `${inx}`,
                    bot_id: 'provider_onboarding',
                    content_type: 'image/png',
                    url: item.url,
                    size: item.file.size,
                    file_type: 'image',
                    filename: item.file.name,
                    base64: '',
                    created_at: dayjs().format('YYYY-MM-DDTHH:mm:ss.SSS'),
                };
                return mapItem;
                //return item.file;
            });
        }
        messageListRef.current.push(newAddMessageFromUser);

        //add thinking message
        const pendingData: TAiMessage = {
            role: EAiRole.ASSISTANT,
            content: '',
            timestamp: dayjs().add(2, 'second').format('YYYY-MM-DDTHH:mm:ss.SSS'),
            bot_id: BOT_ID.PROVIDER_ONBOARDING,
        };
        pendingAIReplyMessage.current = pendingData;

        forceUpdate({});
        scrollToBottom();
        const addImages: TAIUploadImage[] = [];
        const file_ids: string[] = [];
        if (_images && _images.length > 0) {
            for (let i = 0; i < _images.length; i++) {
                try {
                    const file = _images[i];
                    const formData = new FormData();
                    formData.append('file', file);
                    formData.append('bot_id', BOT_ID.PROVIDER_ONBOARDING);
                    // eslint-disable-next-line no-await-in-loop
                    const res = await uploadImage(formData);
                    if (!res.error) {
                        const imageRes: TAIUploadImage = res?.data?.data;
                        if (imageRes.id) {
                            addImages.push(imageRes);
                            file_ids.push(imageRes.id);
                        }
                    }
                } catch (e) {
                    console.error(e);
                }
            }
            newAddMessageFromUser.files = addImages;
            forceUpdate({});
        }

        sendMessageToServer(content, file_ids);

        forceUpdate({});
        scrollToBottom();
    };

    // 发送消息
    const handleClickSendButton = () => {
        if (inputValue.trim() || formatedImages.length > 0) {
            handleClickSendMessage(
                inputValue,
                formatedImages.map((img) => img.file),
            );
            setInputValue('');
            setFormatedImages([]);
            // 清理URL对象
            // formatedImages.forEach((img) => URL.revokeObjectURL(img.url));
        }
    };

    // 键盘快捷键发送
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleClickSendButton();
        }
    };

    const handleClickQuestion = (question: string) => {
        // 检查是否是特定问题，如果是则执行跳转，否则发送消息
        const trimmedQuestion = question.trim().toLowerCase();

        // 检查是否是 "Opening more availability" 相关问题
        if (trimmedQuestion.includes('opening more availability')) {
            navigate(`${PATH.DASHBOARD}/${PATH.CALENDAR_SETTING}`);
            return;
        }

        // 检查是否是 "Try Pro-Listings" 相关问题
        if (trimmedQuestion.includes('try pro-listings')) {
            navigate(`${PATH.DASHBOARD}/${PATH.MARKETING}?tab=prolisting`);
            return;
        }

        // 其他问题正常发送消息
        handleClickSendMessage(question);
    };

    const handleFeedback = async (feedback: EFeedbackResult) => {
        if (feedbackResult !== undefined) {
            return;
        }
        setFeedbackResult(feedback);
        scrollToBottom();
        //setIsFeedbackLoading(true);

        const res = await feedbackMessage({
            bot_id: BOT_ID.PROVIDER_ONBOARDING,
            bot_name: 'Provider Onboarding Bot',
            user_question: latestUserMessage.current || '',
            ai_answer: pendingAIReplyMessage.current?.content || '',
            feedback_type: feedback === EFeedbackResult.LIKE ? 'positive' : 'negative',
            comment: '',
            bot_version: 'v1.0',
        });
        if (!res.error) {
            const messageId = res.data?.data?.id;
            setFeedbackMessageId(messageId);
        }
        //setIsFeedbackLoading(false);
        //result to server
    };

    const handleFeedbackBadReason = async () => {
        // 为了避免类型错误，确保 badReason 一定有值才拼接，否则只用 badReason
        let comment: string | undefined = badReason;

        if (badReason && badReasonComment) {
            comment = `${badReason}--${badReasonComment}`;
        }
        scrollToBottom();
        setIsFeedbackLoading(true);

        const res = await feedbackMessage({
            bot_id: BOT_ID.PROVIDER_ONBOARDING,
            bot_name: 'Provider Onboarding Bot',
            user_question: latestUserMessage.current || '',
            ai_answer: pendingAIReplyMessage.current?.content || '',
            feedback_type: 'negative',
            comment,
            bot_version: 'v1.0',
            message_id: `${feedbackMessageId}`,
        });
        if (!res.error) {
            const messageId = res.data?.data?.id;
            setFeedbackMessageId(messageId);
            setHasFeedbackComment(true);
            setBadReason(undefined);
            setBadReasonComment('');
        }
        setIsFeedbackLoading(false);
    };

    const handleViewImage = (url: string) => {
        setPreviewImageUrl(url);
    };
    // 当message为空时，重置高度
    React.useEffect(() => {
        if (!inputValue) {
            adjustHeight();
        }
    }, [inputValue, adjustHeight]);

    React.useEffect(() => {
        const fetchData = async () => {
            // 如果有初始消息，说明用户已经有明确的问题，不需要立即显示建议问题
            // 建议问题会在 AI 回复完成后显示
            if (pageId && !initialMessage) {
                await fetchSuggestedQuestions(pageId);
            }
        };
        if (pageId && !initialMessage) {
            fetchData();
        }
    }, [pageId, initialMessage, fetchSuggestedQuestions]);

    React.useEffect(() => {
        //moveSuggestToNewMessage();
    }, [suggestedQuestions]);

    React.useEffect(() => {
        const currentMessageList = messageListRef.current
            ?.filter((item) => {
                return item.role === EAiRole.USER || item.role === EAiRole.ASSISTANT;
            })
            .map((item) => {
                const _newItem = { ...item };
                _newItem.blobs = [];
                return _newItem;
            });
        if (currentMessageList && currentMessageList.length > 0) {
            if (pendingAIReplyMessage.current?.content) {
                currentMessageList.push({ ...pendingAIReplyMessage.current, files: [] });
            }
            if (historyMessageListRef.current && historyMessageListRef.current.length > 0) {
                currentMessageList.unshift(...historyMessageListRef.current);
            }
            setAiChatHistoryMessage(currentMessageList);
        }
    }, [forceUpdateFlag]);

    // 自动发送初始消息
    React.useEffect(() => {
        if (
            initialMessage &&
            initialMessage.trim() &&
            !hasSentInitialMessage.current &&
            !isSending &&
            messageListRef.current.length === 0
        ) {
            // 延迟发送，确保组件完全初始化
            const timer = setTimeout(() => {
                if (!hasSentInitialMessage.current) {
                    hasSentInitialMessage.current = true;
                    isInitialMessageReply.current = true; // 标记这是初始消息的回复
                    handleClickSendMessage(initialMessage);
                }
            }, 500);
            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialMessage]);

    return (
        <div className={classNames(s.chatWrap, chatWrapClassName)}>
            <div className={s.header}>
                <div className={s.left}>
                    <img src={Avatar} className={s.avatar} alt="avatar" />
                    Olivia
                    <div className={s.icon}>
                        <img src={Spark} className={s.sp} alt="Spark" />
                        AI Agent
                    </div>
                </div>
                <div
                    onClick={() => {
                        onClose?.();
                    }}
                    className={s.right}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="23" viewBox="0 0 24 23" fill="none">
                        <path
                            d="M6.25 17.25L17.75 5.75M6.25 5.75L17.75 17.25"
                            stroke="#4B5563"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>
            </div>
            <div className={s.messageWrap} id="ai-message-wrap">
                {showHistoryMessage && (
                    <div className={s.historyMessageWrap}>
                        {historyMessageList.map((item, inx) => {
                            let showIcon = false;
                            if (inx === 0) {
                                showIcon = true;
                            } else {
                                const lastItem = messageList[inx - 1];
                                if (
                                    lastItem &&
                                    item.role === EAiRole.USER &&
                                    (lastItem.role === EAiRole.ASSISTANT || lastItem.role === EAiRole.QUESTION)
                                ) {
                                    showIcon = true;
                                }
                                if (
                                    lastItem &&
                                    lastItem.role === EAiRole.USER &&
                                    (item.role === EAiRole.ASSISTANT || item.role === EAiRole.QUESTION)
                                ) {
                                    showIcon = true;
                                }
                                if (lastItem && lastItem.role === EAiRole.QUESTION) {
                                    showIcon = true;
                                }
                            }
                            let showTime = false;
                            if (inx === 0) {
                                showIcon = true;
                            } else {
                                const lastItem = messageList[inx - 1];
                                if (
                                    lastItem &&
                                    item.role === EAiRole.USER &&
                                    (lastItem.role === EAiRole.ASSISTANT || lastItem.role === EAiRole.QUESTION)
                                ) {
                                    showTime = true;
                                }
                                if (
                                    lastItem &&
                                    lastItem.role === EAiRole.USER &&
                                    (item.role === EAiRole.ASSISTANT || item.role === EAiRole.QUESTION)
                                ) {
                                    showTime = true;
                                }
                            }
                            if (item.role === EAiRole.QUESTION) {
                                showTime = true;
                                showIcon = true;
                            }

                            return (
                                <MessageItem
                                    onClickQuestion={handleClickQuestion}
                                    key={item.timestamp}
                                    item={item}
                                    showIcon={showIcon}
                                    showTime={showTime}
                                    onClickImage={handleViewImage}
                                />
                            );
                        })}
                    </div>
                )}
                {hasHistoryMessageList && (
                    <div className={s.historyMessageSwitch}>
                        {!showHistoryMessage && (
                            <div
                                onClick={() => {
                                    setShowHistoryMessage(true);
                                    currentScrollBottomRef.current =
                                        document.getElementById('ai-message-wrap')?.scrollHeight || 0;
                                }}
                                className={s.historyMessageSwitchItem}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="11"
                                    height="6"
                                    viewBox="0 0 11 6"
                                    fill="none"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M0.792893 0.292893C1.18342 -0.097631 1.81658 -0.097631 2.2071 0.292893L5.49999 3.58579L8.79288 0.292893C9.1834 -0.0976311 9.81657 -0.0976311 10.2071 0.292893C10.5976 0.683417 10.5976 1.31658 10.2071 1.70711L6.2071 5.70711C5.81657 6.09763 5.18341 6.09763 4.79289 5.70711L0.792893 1.70711C0.402369 1.31658 0.402369 0.683417 0.792893 0.292893Z"
                                        fill="#374151"
                                    />
                                </svg>
                                <span className={s.switchText}>Show previous chats</span>
                            </div>
                        )}
                        {showHistoryMessage && (
                            <div
                                onClick={() => {
                                    setShowHistoryMessage(false);
                                }}
                                className={s.historyMessageSwitchItem}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="21"
                                    height="20"
                                    viewBox="0 0 21 20"
                                    fill="none"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M15.2071 12.7071C14.8166 13.0976 14.1834 13.0976 13.7929 12.7071L10.5 9.41421L7.20712 12.7071C6.8166 13.0976 6.18343 13.0976 5.79291 12.7071C5.40238 12.3166 5.40238 11.6834 5.79291 11.2929L9.7929 7.29289C10.1834 6.90237 10.8166 6.90237 11.2071 7.29289L15.2071 11.2929C15.5976 11.6834 15.5976 12.3166 15.2071 12.7071Z"
                                        fill="#374151"
                                    />
                                </svg>
                                <span className={s.switchText}>Hide previous chats</span>
                            </div>
                        )}
                    </div>
                )}
                {messageList.map((item, inx) => {
                    let showIcon = false;
                    if (inx === 0) {
                        showIcon = true;
                    } else {
                        const lastItem = messageList[inx - 1];
                        if (
                            lastItem &&
                            item.role === EAiRole.USER &&
                            (lastItem.role === EAiRole.ASSISTANT || lastItem.role === EAiRole.QUESTION)
                        ) {
                            showIcon = true;
                        }
                        if (
                            lastItem &&
                            lastItem.role === EAiRole.USER &&
                            (item.role === EAiRole.ASSISTANT || item.role === EAiRole.QUESTION)
                        ) {
                            showIcon = true;
                        }
                        if (lastItem && lastItem.role === EAiRole.QUESTION) {
                            showIcon = true;
                        }
                    }
                    let showTime = false;
                    if (inx === 0) {
                        showTime = true;
                    } else {
                        const lastItem = messageList[inx - 1];
                        if (
                            lastItem &&
                            item.role === EAiRole.USER &&
                            (lastItem.role === EAiRole.ASSISTANT || lastItem.role === EAiRole.QUESTION)
                        ) {
                            showTime = true;
                        }
                        if (
                            lastItem &&
                            lastItem.role === EAiRole.USER &&
                            (item.role === EAiRole.ASSISTANT || item.role === EAiRole.QUESTION)
                        ) {
                            showTime = true;
                        }
                    }
                    if (item.role === EAiRole.QUESTION) {
                        showIcon = true;
                        showTime = true;
                    }

                    return (
                        <MessageItem
                            onClickQuestion={handleClickQuestion}
                            key={item.timestamp}
                            onClickImage={handleViewImage}
                            item={item}
                            showIcon={showIcon}
                            showTime={showTime}
                        />
                    );
                })}
                {/* {
                    suggestionMessage && (
                        <div className={s.suggestionWrap}>
                            <MessageItem
                                onClickQuestion={handleClickQuestion}
                                key={suggestionMessage.timestamp}
                                item={suggestionMessage}
                                showIcon
                                showTime
                            />
                        </div>
                    )
                } */}
                <div className={`${s.extraMsgWrap} ${revertExtraMsg ? s.extraMsgWrapRevert : ''}`}>
                    {pendingAIReplyMessage.current && (
                        <div className={s.pendingItemWrap}>
                            <div className={s.pendingItem}>
                                <div className={s.avatarWrap}>
                                    <img src={Avatar} alt="avatar" />
                                </div>
                                {pendingAIReplyMessage.current.content && (
                                    <div className={s.content}>
                                        <MarkdownRenderer
                                            size="small"
                                            content={pendingAIReplyMessage.current.content || ''}
                                        />
                                    </div>
                                )}
                                {!pendingAIReplyMessage.current.content && (
                                    <div className={s.content}>
                                        <ChatLoading />
                                    </div>
                                )}
                            </div>
                            {showFeedback && (
                                <div className={s.feedbackBtn}>
                                    {feedbackResult === EFeedbackResult.LIKE ? (
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="20"
                                            height="20"
                                            viewBox="0 0 20 20"
                                            fill="none"
                                        >
                                            <path
                                                d="M1.74531 9.23842C1.73532 9.12336 1.74936 9.00748 1.78653 8.89813C1.8237 8.78878 1.88319 8.68836 1.96124 8.60322C2.03928 8.51809 2.13417 8.45011 2.23988 8.40359C2.34559 8.35707 2.45982 8.33303 2.57531 8.33301H4.16656C4.38757 8.33301 4.59954 8.4208 4.75582 8.57708C4.9121 8.73336 4.99989 8.94533 4.99989 9.16634V17.083C4.99989 17.304 4.9121 17.516 4.75582 17.6723C4.59954 17.8285 4.38757 17.9163 4.16656 17.9163H3.26406C3.05549 17.9164 2.85448 17.8382 2.70074 17.6973C2.54699 17.5563 2.45169 17.3629 2.43364 17.1551L1.74531 9.23842ZM7.49989 8.90592C7.49989 8.55759 7.71656 8.24592 8.03114 8.09759C8.71823 7.77342 9.88864 7.12217 10.4166 6.24176C11.097 5.10676 11.2253 3.05634 11.2461 2.58676C11.2491 2.52092 11.2474 2.45509 11.2561 2.39009C11.3691 1.57634 12.9395 2.52676 13.5416 3.53176C13.8686 4.07676 13.9103 4.79301 13.8761 5.35259C13.8391 5.95092 13.6636 6.52884 13.4916 7.10301L13.1249 8.32676H17.6486C17.7774 8.32675 17.9044 8.35657 18.0197 8.41389C18.135 8.47121 18.2354 8.55447 18.3131 8.65713C18.3908 8.75978 18.4437 8.87905 18.4676 9.00557C18.4915 9.13209 18.4857 9.26242 18.4507 9.38634L16.2132 17.3097C16.1638 17.4844 16.0587 17.6383 15.9138 17.7478C15.769 17.8573 15.5923 17.9165 15.4107 17.9163H8.33323C8.11221 17.9163 7.90025 17.8285 7.74397 17.6723C7.58769 17.516 7.49989 17.304 7.49989 17.083V8.90592Z"
                                                fill="#1F2937"
                                                stroke="#1F2937"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    ) : (
                                        <span
                                            className={s.like}
                                            onClick={() => {
                                                handleFeedback(EFeedbackResult.LIKE);
                                            }}
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="20"
                                                height="20"
                                                viewBox="0 0 20 20"
                                                fill="none"
                                            >
                                                <path
                                                    d="M1.74531 9.23842C1.73532 9.12336 1.74936 9.00748 1.78653 8.89813C1.8237 8.78879 1.88319 8.68836 1.96124 8.60322C2.03928 8.51809 2.13417 8.45011 2.23988 8.40359C2.34559 8.35707 2.45982 8.33303 2.57531 8.33301H4.16656C4.38757 8.33301 4.59954 8.4208 4.75582 8.57708C4.9121 8.73336 4.99989 8.94533 4.99989 9.16634V17.083C4.99989 17.304 4.9121 17.516 4.75582 17.6723C4.59954 17.8285 4.38757 17.9163 4.16656 17.9163H3.26406C3.05549 17.9164 2.85448 17.8382 2.70074 17.6973C2.54699 17.5563 2.45169 17.3629 2.43364 17.1551L1.74531 9.23842ZM7.49989 8.90592C7.49989 8.55759 7.71656 8.24592 8.03114 8.09759C8.71823 7.77342 9.88864 7.12217 10.4166 6.24176C11.097 5.10676 11.2253 3.05634 11.2461 2.58676C11.2491 2.52092 11.2474 2.45509 11.2561 2.39009C11.3691 1.57634 12.9395 2.52676 13.5416 3.53176C13.8686 4.07676 13.9103 4.79301 13.8761 5.35259C13.8391 5.95092 13.6636 6.52884 13.4916 7.10301L13.1249 8.32676H17.6486C17.7774 8.32675 17.9044 8.35657 18.0197 8.41389C18.135 8.47121 18.2354 8.55447 18.3131 8.65713C18.3908 8.75978 18.4437 8.87905 18.4676 9.00557C18.4915 9.13209 18.4857 9.26242 18.4507 9.38634L16.2132 17.3097C16.1638 17.4844 16.0587 17.6383 15.9138 17.7478C15.769 17.8573 15.5923 17.9165 15.4107 17.9163H8.33323C8.11221 17.9163 7.90025 17.8285 7.74397 17.6723C7.58769 17.516 7.49989 17.304 7.49989 17.083V8.90592Z"
                                                    stroke="#9CA3AF"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                    )}
                                    {feedbackResult === EFeedbackResult.UNLIKE ? (
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="20"
                                            height="18"
                                            viewBox="0 0 20 18"
                                            fill="none"
                                        >
                                            <path
                                                d="M1.74531 9.76158C1.73532 9.87664 1.74936 9.99252 1.78653 10.1019C1.8237 10.2112 1.88319 10.3116 1.96124 10.3968C2.03928 10.4819 2.13417 10.5499 2.23988 10.5964C2.34559 10.6429 2.45982 10.667 2.57531 10.667H4.16656C4.38757 10.667 4.59954 10.5792 4.75582 10.4229C4.9121 10.2666 4.99989 10.0547 4.99989 9.83366L4.99989 1.917C4.99989 1.69598 4.9121 1.48402 4.75582 1.32774C4.59954 1.17146 4.38757 1.08366 4.16656 1.08366H3.26406C3.05549 1.08361 2.85448 1.16177 2.70074 1.30271C2.54699 1.44365 2.45169 1.63712 2.43364 1.84491L1.74531 9.76158ZM7.49989 10.0941C7.49989 10.4424 7.71656 10.7541 8.03114 10.9024C8.71823 11.2266 9.88864 11.8778 10.4166 12.7582C11.097 13.8932 11.2253 15.9437 11.2461 16.4132C11.2491 16.4791 11.2474 16.5449 11.2561 16.6099C11.3691 17.4237 12.9395 16.4732 13.5416 15.4682C13.8686 14.9232 13.9103 14.207 13.8761 13.6474C13.8391 13.0491 13.6636 12.4712 13.4916 11.897L13.1249 10.6732H17.6486C17.7774 10.6733 17.9044 10.6434 18.0197 10.5861C18.135 10.5288 18.2354 10.4455 18.3131 10.3429C18.3908 10.2402 18.4437 10.1209 18.4676 9.99443C18.4915 9.86791 18.4857 9.73758 18.4507 9.61366L16.2132 1.69033C16.1638 1.51558 16.0587 1.36175 15.9138 1.25224C15.769 1.14273 15.5923 1.08353 15.4107 1.08366L8.33323 1.08366C8.11221 1.08366 7.90025 1.17146 7.74397 1.32774C7.58769 1.48402 7.49989 1.69598 7.49989 1.917L7.49989 10.0941Z"
                                                fill="#1F2937"
                                                stroke="#1F2937"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    ) : (
                                        <span
                                            className={s.unlike}
                                            onClick={() => {
                                                handleFeedback(EFeedbackResult.UNLIKE);
                                            }}
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="20"
                                                height="20"
                                                viewBox="0 0 20 20"
                                                fill="none"
                                            >
                                                <path
                                                    d="M1.74213 11.1805C1.73256 11.2954 1.74695 11.4111 1.78439 11.5202C1.82183 11.6293 1.8815 11.7294 1.95964 11.8142C2.03777 11.8991 2.13266 11.9668 2.23831 12.013C2.34395 12.0593 2.45805 12.0831 2.57338 12.083H4.16713C4.38815 12.083 4.60011 11.9952 4.75639 11.8389C4.91267 11.6827 5.00047 11.4707 5.00047 11.2497V2.91634C5.00047 2.69533 4.91267 2.48337 4.75639 2.32709C4.60011 2.17081 4.38815 2.08301 4.16713 2.08301H3.26713C3.0581 2.08302 2.8567 2.16159 2.70289 2.30313C2.54907 2.44468 2.45407 2.63886 2.43672 2.84717L1.74213 11.1805ZM7.50047 11.0938C7.50047 11.4418 7.71713 11.7534 8.03172 11.9022C8.7188 12.2259 9.88922 12.8772 10.4171 13.758C11.0975 14.8926 11.2259 16.943 11.2467 17.4126C11.2496 17.4784 11.248 17.5443 11.2567 17.6093C11.3696 18.423 12.94 17.4726 13.5421 16.4676C13.8692 15.9226 13.9109 15.2063 13.8767 14.6468C13.8396 14.0488 13.6642 13.4705 13.4921 12.8963L13.1255 11.6726H17.6492C17.778 11.6726 17.905 11.6428 18.0203 11.5855C18.1355 11.5281 18.236 11.4449 18.3137 11.3422C18.3914 11.2396 18.4443 11.1203 18.4681 10.9938C18.492 10.8673 18.4863 10.7369 18.4513 10.613L16.2138 2.68967C16.1644 2.51493 16.0593 2.36109 15.9144 2.25158C15.7696 2.14207 15.5929 2.08288 15.4113 2.08301H8.3338C8.11279 2.08301 7.90082 2.17081 7.74454 2.32709C7.58826 2.48337 7.50047 2.69533 7.50047 2.91634V11.0938Z"
                                                    stroke="#9CA3AF"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                    )}
                                </div>
                            )}
                            {feedbackResult === EFeedbackResult.UNLIKE && !hasFeedbackComment && (
                                <div className={s.badReason}>
                                    <div className={s.question}>
                                        <span>Tell us more:</span>
                                        <span
                                            onClick={() => {
                                                setHasFeedbackComment(true);
                                            }}
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="20"
                                                height="20"
                                                viewBox="0 0 20 20"
                                                fill="none"
                                            >
                                                <path
                                                    d="M5 15L15 5M5 5L15 15"
                                                    stroke="#4B5563"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                    </div>
                                    <div className={s.answerList}>
                                        <div
                                            className={`${s.answerItem} ${badReason === EBadReason.ANSWER_INCORRECT ? s.active : ''}`}
                                            onClick={() => setBadReason(EBadReason.ANSWER_INCORRECT)}
                                        >
                                            {EBadReason.ANSWER_INCORRECT}
                                        </div>
                                        <div
                                            className={`${s.answerItem} ${badReason === EBadReason.NOT_DETAILED_ENOUGH ? s.active : ''}`}
                                            onClick={() => setBadReason(EBadReason.NOT_DETAILED_ENOUGH)}
                                        >
                                            {EBadReason.NOT_DETAILED_ENOUGH}
                                        </div>
                                        <div
                                            className={`${s.answerItem} ${badReason === EBadReason.DID_NOT_ADDRESS_MY_QUESTION ? s.active : ''}`}
                                            onClick={() => setBadReason(EBadReason.DID_NOT_ADDRESS_MY_QUESTION)}
                                        >
                                            {EBadReason.DID_NOT_ADDRESS_MY_QUESTION}
                                        </div>
                                        <div
                                            className={`${s.answerItem} ${badReason === EBadReason.HARD_TO_UNDERSTAND ? s.active : ''}`}
                                            onClick={() => setBadReason(EBadReason.HARD_TO_UNDERSTAND)}
                                        >
                                            {EBadReason.HARD_TO_UNDERSTAND}
                                        </div>
                                    </div>
                                    <TextArea
                                        onChange={(e) => {
                                            setBadReasonComment(e.target.value);
                                        }}
                                        value={badReasonComment}
                                        placeholder="Additional feedback (optional)"
                                        rows={4}
                                        maxLength={2000}
                                        className={s.textarea}
                                    />
                                    <div className={s.rBtn}>
                                        <Button
                                            onClick={() => {
                                                setHasFeedbackComment(true);
                                            }}
                                            className={s.cancelBtn}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            className={s.submitBtn}
                                            type="primary"
                                            loading={isFeedbackLoading}
                                            onClick={handleFeedbackBadReason}
                                            disabled={!badReason}
                                        >
                                            Submit
                                        </Button>
                                    </div>
                                </div>
                            )}
                            {hasFeedbackComment && (
                                <div className={s.thanksMsg}>
                                    Thanks for your feedback! We’ll use it to improve future answers.
                                </div>
                            )}
                        </div>
                    )}
                    {pendingSuggestedMessage.current &&
                        (pendingSuggestedMessage.current.content ? (
                            <MessageItem
                                onClickQuestion={handleClickQuestion}
                                key={pendingSuggestedMessage.current.timestamp}
                                onClickImage={handleViewImage}
                                item={pendingSuggestedMessage.current}
                                showIcon
                                showTime
                            />
                        ) : (
                            <div className={s.pendingItemWrap}>
                                <div className={s.pendingItem}>
                                    <div className={s.avatarWrap}>
                                        <img src={Avatar} alt="avatar" />
                                    </div>
                                    <div className={s.content}>
                                        <ChatLoading />
                                    </div>
                                </div>
                            </div>
                        ))}
                </div>
            </div>
            <div className={s.inputWrap}>
                <div className={s.inputContainer}>
                    {/* 图片预览区域 */}
                    {formatedImages.length > 0 && (
                        <div className={s.imagePreview}>
                            {formatedImages.map((image) => (
                                <div key={image.id} className={s.imageItem}>
                                    <img src={image.url} alt="pasted" className={s.previewImg} />
                                    <button onClick={() => removeImage(image.id)} className={s.removeBtn} type="button">
                                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                            <path
                                                d="M9 3L3 9M3 3L9 9"
                                                stroke="currentColor"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* 隐藏的文件输入 */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                    />

                    {/* 输入框区域 */}
                    <div className={s.inputArea}>
                        <textarea
                            ref={textareaRef}
                            value={inputValue}
                            onChange={handleInputChange}
                            onPaste={handlePaste}
                            onKeyDown={handleKeyDown}
                            placeholder="Ask me anything about onboarding..."
                            className={s.textarea}
                            rows={1}
                        />

                        {/* 按钮行 */}
                        <Spin spinning={isSending}>
                            <div className={s.buttonRow}>
                                <button className={s.addBtn} type="button" onClick={handleAddClick}>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="22"
                                        height="18"
                                        viewBox="0 0 22 18"
                                        fill="none"
                                    >
                                        <path
                                            d="M20.75 0.75H1.25C0.835156 0.75 0.5 1.08516 0.5 1.5V16.5C0.5 16.9148 0.835156 17.25 1.25 17.25H20.75C21.1648 17.25 21.5 16.9148 21.5 16.5V1.5C21.5 1.08516 21.1648 0.75 20.75 0.75ZM19.8125 15.5625H2.1875V14.6273L5.43359 10.7766L8.95156 14.9484L14.4242 8.46094L19.8125 14.85V15.5625ZM19.8125 12.5203L14.5672 6.3C14.4922 6.21094 14.3562 6.21094 14.2812 6.3L8.95156 12.6188L5.57656 8.61797C5.50156 8.52891 5.36562 8.52891 5.29062 8.61797L2.1875 12.2977V2.4375H19.8125V12.5203ZM6.125 7.6875C6.39585 7.6875 6.66405 7.63415 6.91428 7.5305C7.16452 7.42685 7.39189 7.27493 7.58341 7.08341C7.77493 6.89189 7.92685 6.66452 8.0305 6.41428C8.13415 6.16405 8.1875 5.89585 8.1875 5.625C8.1875 5.35415 8.13415 5.08595 8.0305 4.83572C7.92685 4.58548 7.77493 4.35811 7.58341 4.16659C7.39189 3.97507 7.16452 3.82315 6.91428 3.7195C6.66405 3.61585 6.39585 3.5625 6.125 3.5625C5.57799 3.5625 5.05339 3.7798 4.66659 4.16659C4.2798 4.55339 4.0625 5.07799 4.0625 5.625C4.0625 6.17201 4.2798 6.69661 4.66659 7.08341C5.05339 7.4702 5.57799 7.6875 6.125 7.6875ZM6.125 4.96875C6.48828 4.96875 6.78125 5.26172 6.78125 5.625C6.78125 5.98828 6.48828 6.28125 6.125 6.28125C5.76172 6.28125 5.46875 5.98828 5.46875 5.625C5.46875 5.26172 5.76172 4.96875 6.125 4.96875Z"
                                            fill="#9CA3AF"
                                        />
                                    </svg>
                                </button>

                                <div className={s.buttonSpacer} />

                                {/* <button className={s.voiceBtn} type="button">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M12 2.09961C10.9129 2.09961 9.87039 2.52175 9.10172 3.27315C8.33305 4.02456 7.90122 5.04369 7.90122 6.10634V10.7645C7.90122 11.2907 8.00724 11.8117 8.21322 12.2978C8.4192 12.784 8.72112 13.2257 9.10172 13.5977C9.48233 13.9698 9.93418 14.2649 10.4315 14.4663C10.9288 14.6676 11.4617 14.7713 12 14.7713C12.5383 14.7713 13.0712 14.6676 13.5685 14.4663C14.0658 14.2649 14.5177 13.9698 14.8983 13.5977C15.2789 13.2257 15.5808 12.784 15.7868 12.2978C15.9928 11.8117 16.0988 11.2907 16.0988 10.7645V6.10634C16.0988 5.04369 15.6669 4.02456 14.8983 3.27315C14.1296 2.52175 13.0871 2.09961 12 2.09961ZM10.9565 18.4312V20.0796C10.9565 20.3501 11.0665 20.6096 11.2621 20.8008C11.4578 20.9921 11.7233 21.0996 12 21.0996C12.2767 21.0996 12.5422 20.9921 12.7379 20.8008C12.9335 20.6096 13.0435 20.3501 13.0435 20.0796V18.4298C14.9184 18.3424 16.6869 17.5529 17.9821 16.2249C19.2774 14.897 20 13.1325 20 11.2977C20 11.0271 19.8901 10.7677 19.6944 10.5764C19.4987 10.3851 19.2333 10.2776 18.9565 10.2776C18.6798 10.2776 18.4144 10.3851 18.2187 10.5764C18.023 10.7677 17.913 11.0271 17.913 11.2977C17.913 12.6503 17.3634 13.9476 16.3849 14.9041C15.4065 15.8605 14.0794 16.3979 12.6957 16.3979H11.3043C9.92061 16.3979 8.59355 15.8605 7.6151 14.9041C6.63664 13.9476 6.08696 12.6503 6.08696 11.2977C6.08696 11.1637 6.05997 11.0311 6.00753 10.9073C5.95509 10.7836 5.87823 10.6711 5.78133 10.5764C5.68443 10.4817 5.5694 10.4065 5.4428 10.3553C5.3162 10.304 5.18051 10.2776 5.04348 10.2776C4.90645 10.2776 4.77076 10.304 4.64416 10.3553C4.51756 10.4065 4.40252 10.4817 4.30563 10.5764C4.20873 10.6711 4.13187 10.7836 4.07943 10.9073C4.02699 11.0311 4 11.1637 4 11.2977C3.99966 13.1328 4.72209 14.8975 6.01739 16.2258C7.31269 17.554 9.0814 18.3438 10.9565 18.4312Z" fill="currentColor" />
                                </svg>
                            </button> */}

                                <button
                                    onClick={handleClickSendButton}
                                    disabled={(!inputValue.trim() && formatedImages.length === 0) || isTyping}
                                    className={s.sendBtn}
                                    type="button"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="25"
                                        viewBox="0 0 24 25"
                                        fill="none"
                                    >
                                        <path
                                            d="M20.2346 5.78521C20.6666 4.59021 19.5086 3.43221 18.3136 3.86521L3.70855 9.14721C2.50955 9.58121 2.36455 11.2172 3.46755 11.8562L8.12955 14.5552L12.2926 10.3922C12.4812 10.2101 12.7338 10.1093 12.996 10.1115C13.2582 10.1138 13.509 10.219 13.6944 10.4044C13.8798 10.5898 13.9849 10.8406 13.9872 11.1028C13.9895 11.365 13.8887 11.6176 13.7066 11.8062L9.54355 15.9692L12.2436 20.6312C12.8816 21.7342 14.5176 21.5882 14.9516 20.3902L20.2346 5.78521Z"
                                            fill="currentColor"
                                        />
                                    </svg>
                                </button>
                            </div>
                        </Spin>
                    </div>
                </div>
            </div>
            <Modal
                title={null}
                closeIcon={null}
                width={520}
                onCancel={() => {
                    setPreviewImageUrl(undefined);
                }}
                open={!!previewImageUrl}
                footer={null}
                wrapClassName={s.previewModal}
            >
                <div className={s.previewImage}>
                    <img src={previewImageUrl} alt="preview" />
                </div>
            </Modal>
        </div>
    );
}
